/**
 * INV.ESTABLISH guarantee (I1) — F-algebra structure.
 *
 * Neutralizes time reversal (G3) by requiring safety invariants to be
 * established before any state transition. The initial state must satisfy
 * all invariants, proved by the algebra's initial algebra property.
 *
 * Agda source: Kernel/INV/ESTABLISH/Neutralization.agda
 * Realization fidelity: STRUCTURAL (invariant checking enforced; algebra laws documented)
 * Safety class: Parametric (acquired immunity)
 * Neutralization mode: INERT — violations exist but cannot compose into exploits
 * Tier: 2 (structural-geometric)
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type { Brand, Guarantee } from "../foundation/types.js";
import { INV_ESTABLISH_META } from "../shared/theorem-meta.js";
export { INV_ESTABLISH_META };

// ── Structure: F-algebra ───────────────────────────────────────

/** A state with established invariants. */
export type EstablishedState<S> = Brand<S, "EstablishedState">;

/** An invariant that must hold for a state to be valid. */
export interface Invariant<S> {
  readonly name: string;
  readonly check: (state: S) => boolean;
}

/** Establish invariants for an initial state. Throws if any invariant fails. */
export function establish<S>(state: S, invariants: readonly Invariant<S>[]): EstablishedState<S> {
  for (const inv of invariants) {
    if (!inv.check(state)) {
      throw new Error(
        `INV.ESTABLISH: invariant "${inv.name}" not satisfied — ` +
          "state cannot enter the system without established invariants",
      );
    }
  }
  return state as EstablishedState<S>;
}

export interface InvEstablishViolation {
  readonly __brand: "InvEstablishViolation";
  readonly kind: "unestablished-invariant";
}

export interface InvEstablishStructure<S> {
  readonly invariants: readonly Invariant<S>[];
  readonly initialState: S;
}

export function createInvEstablishGuarantee<S>(
  structure: InvEstablishStructure<S>,
): Guarantee<InvEstablishStructure<S>, InvEstablishViolation> {
  return {
    structure,
    neutralize: (_violation: InvEstablishViolation): never => {
      throw new Error(
        "INV.ESTABLISH violation is structurally inert: " +
          "establish() enforces all invariants before state enters the system",
      );
    },
    meta: INV_ESTABLISH_META,
  };
}
